import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-slate-950">
        <img
          src="https://images.unsplash.com/photo-1589554882513-691f8f071f72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Clinic"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
        <div className="relative flex items-center gap-3 text-white">
          <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="leading-tight">
            <p className="font-display font-extrabold tracking-tight">Youngness</p>
            <p className="text-[11px] uppercase tracking-wider text-white/60">CEO Command Center</p>
          </div>
        </div>
        <div className="relative text-white max-w-md">
          <h1 className="font-display text-4xl font-extrabold tracking-tight leading-tight">
            One command center for the entire Youngness group.
          </h1>
          <p className="mt-4 text-white/70 leading-relaxed">
            Delhi HQ · Jaipur · Mathura · Institute — assets, departments, goals and
            profitability, all in a single executive view.
          </p>
        </div>
        <p className="relative text-xs text-white/40">© {new Date().getFullYear()} Dr. Awish's Youngness Group</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <p className="font-display font-extrabold">Youngness Command Center</p>
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="text-muted-foreground text-sm mt-1">Welcome back. Enter your credentials to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@youngness.com" required data-testid="login-email-input" autoComplete="email" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                  data-testid="login-password-input" autoComplete="current-password" className="pr-10" />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="toggle-password-visibility">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-rose-600 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-xs text-muted-foreground text-center leading-relaxed">
            Access is managed by the CEO. Contact your administrator to request an account.
          </p>
        </div>
      </div>
    </div>
  );
}
